import { updateDisplayLogoInfoPayload } from '../../../../interfaces/chat';
import { appState } from '../../../../interfaces/common';
import { Checkbox, CheckboxGroup, Flex, Image, Text } from '@pega/cosmos-react-core';
import React from 'react';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { InteractiveText } from '../../../Utilities/custom.styled';
import { getLogoMetaData, updateDisplayLogoInfo, validateUploadLogo } from '../ChatApis';

interface UploadLogoProps {
  isMultiExpExpanded: boolean;
}

const logoNoteStyle = { width: '45%' };

const UploadLogo: React.FC<UploadLogoProps> = ({ isMultiExpExpanded }) => {
  const fileRef = useRef<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translate = useLocalTranslation();
  const {
    chatInfo: { connectionId, logo, displayLogo },
    sessionId,
  } = useSelector((state: appState) => {
    return {
      chatInfo: state.chatInfo,
      sessionId: state.sessionId,
    };
  });

  const validateFile = async (requestId: string) => {
    let response;
    try {
      response = await validateUploadLogo(sessionId, connectionId, requestId);
    } catch (error) {
      console.error(error);
      dispatch(updateBannerInfo(translate('logo_upload_failed'), 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      dispatch(setChatInfo(response.data));
      dispatch(updateBannerInfo(translate('logo_upload_success'), 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('logo_upload_failed'), 'urgent'));
    }
  };

  const uploadFile = async (urlAndFields: any, file: File) => {
    const { url, fields } = urlAndFields;
    const requestId = fields['x-amz-meta-request-id'];
    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append('file', file);
    await fetch(url, {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        if (requestId) {
          validateFile(requestId);
        }
      })
      .catch(() => {
        dispatch(updateLoaderDetails(false));
        dispatch(updateBannerInfo(translate('logo_upload_failed'), 'urgent'));
      });
  };

  const getUploadUrl = async (file: File) => {
    dispatch(updateLoaderDetails(true));
    let response = await getLogoMetaData(sessionId, connectionId);
    if (response?.status === 200) {
      uploadFile(response.data, file);
    } else if (response?.status === 403) {
      dispatch(updateLoaderDetails(false));
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateLoaderDetails(false));
      dispatch(updateBannerInfo(translate('logo_upload_failed'), 'urgent'));
    }
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      const file = event.currentTarget.files[0];
      if (file.type.startsWith('image')) {
        getUploadUrl(file);
      }
    }
  };

  const showCompanyLogo = async (e: any) => {
    let payload: updateDisplayLogoInfoPayload = { display: e.target.checked };
    dispatch(updateLoaderDetails(true));
    let json = await updateDisplayLogoInfo(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('logo_update_success'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('logo_update_failed'), 'urgent'));
    }
  };

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 2,
      }}
    >
      <Text variant="h3" as="h3">
        {translate('company_logo')}
      </Text>
      <CheckboxGroup name="info">
        <Checkbox label={translate('display_company_logo')} checked={displayLogo} onChange={showCompanyLogo} />
      </CheckboxGroup>
      <Flex container={{ direction: 'row', itemGap: 1 }}>
        {logo ? (
          <Image
            src={logo}
            alt="widget-logo"
            style={{
              height: 36,
              width: 36,
              borderRadius: '50%',
            }}
          />
        ) : (
          <Flex
            style={{
              height: 40,
              width: 40,
              borderRadius: '50%',
              backgroundColor: '#d8d8d8',
            }}
          ></Flex>
        )}
        <Flex container={{ direction: 'column' }}>
          <Text>
            <InteractiveText
              className="pointer"
              onClick={() => {
                fileRef?.current.click();
              }}
            >
              {translate('upload_image')}
            </InteractiveText>
          </Text>

          <input type="file" accept="image/*" ref={fileRef} onChange={onFileChange} style={{ display: 'none' }} />
          <Text variant="secondary" style={isMultiExpExpanded ? logoNoteStyle : {}}>
            {translate('company_logo_criteria')}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default UploadLogo;
